import { faRotateLeft, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ICorporationResponse } from '@kcftech/prometheus-client';
import { StatelessPopup } from 'components/popup/stateless_popup/StatelessPopup';
import { SearchInput } from 'components/search/SearchInput';
import { usePopup } from 'custom_hooks/usePopup';
import { useEffect, useRef } from 'react';
import { pointerDownClickHandler } from 'utils/pointer_events';
import { ProgressiveList } from '../components/ProgressiveList';
import { CorpTreeItem } from './CorpTreeItem';
import {
    PopupContent,
    SearchWrapper,
    SectionDivider,
    SectionLabel,
    SelectionActionButton,
    SelectionActions,
    TreeContainer,
    TriggerButton,
} from './MissionControlEntityNav.styled';
import { MissionControlOrgs } from './mission_control_entity_store';
import { SelectionSummary } from './SelectionSummary';
import { CorpSearchProvider, useCorpSearchFilter } from './search_state';
import {
    type FavoriteEntityInfo,
    useCorporationsWithFavorites,
    useFavoriteEntityInfo,
    useInitializeStore,
} from './useFavoriteEntityInfo';

export function MissionControlEntityNav() {
    const favorites = useFavoriteEntityInfo();
    const corporationIds = MissionControlOrgs.useCorporationIds();
    const extraLocationIds = MissionControlOrgs.useQueryLocationIds();

    // ordering: register subscriptions ^ before patching in initial state v
    const { corporations, favoriteCorps, otherCorps } =
        useCorporationsWithFavorites(favorites);

    useInitializeStore(favorites, corporations);

    const { isOpen, toggleOpen, close, triggerRef, popupRef } = usePopup();
    const triggerHandlers = pointerDownClickHandler(toggleOpen);

    return (
        <>
            <TriggerButton
                type="button"
                ref={triggerRef}
                {...triggerHandlers}
                aria-label="Select organizations"
            >
                <SelectionSummary
                    corporationIds={corporationIds}
                    extraLocationIds={extraLocationIds}
                />
            </TriggerButton>

            {isOpen && (
                <StatelessPopup
                    anchor={triggerRef.current}
                    ref={popupRef}
                    onClose={close}
                    popupClassName=""
                >
                    <CorpSearchProvider corporations={corporations}>
                        <CorpSearchPopupContent
                            favorites={favorites}
                            favoriteCorps={favoriteCorps}
                            otherCorps={otherCorps}
                        />
                    </CorpSearchProvider>
                </StatelessPopup>
            )}
        </>
    );
}

type CorpSearchPopupContentProps = Readonly<{
    favorites: FavoriteEntityInfo;
    favoriteCorps: ICorporationResponse[];
    otherCorps: ICorporationResponse[];
}>;

function CorpSearchPopupContent({
    favorites,
    favoriteCorps,
    otherCorps,
}: CorpSearchPopupContentProps) {
    const { filter, setFilter } = useCorpSearchFilter();
    const corporationIds = MissionControlOrgs.useCorporationIds();
    const locationIds = MissionControlOrgs.useLocationIds();
    const isCustomized = MissionControlOrgs.useIsCustomized();
    const resetToFavorites = MissionControlOrgs.useResetToFavorites();
    const clearSelection = MissionControlOrgs.useClearSelection();
    const hasSelection = corporationIds.size > 0 || locationIds.size > 0;
    const searchRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        window.setTimeout(() => searchRef.current?.focus());
    }, []);

    return (
        <PopupContent>
            <SearchWrapper>
                <SearchInput
                    ref={searchRef}
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    placeholder="Search organizations"
                />
                <SelectionActions>
                    <SelectionActionButton
                        type="button"
                        onClick={() => resetToFavorites(favorites)}
                        disabled={!isCustomized}
                        title="Reset to favorites"
                    >
                        <FontAwesomeIcon icon={faRotateLeft} />
                        Favorites
                    </SelectionActionButton>
                    <SelectionActionButton
                        type="button"
                        onClick={clearSelection}
                        disabled={!hasSelection}
                        title="Clear selection"
                    >
                        <FontAwesomeIcon icon={faXmark} />
                        Clear
                    </SelectionActionButton>
                </SelectionActions>
            </SearchWrapper>
            <TreeContainer>
                {favoriteCorps.length > 0 && (
                    <>
                        <SectionLabel>Favorites</SectionLabel>
                        {favoriteCorps.map((corp) => (
                            <CorpTreeItem key={corp.Id} corporation={corp} />
                        ))}
                        {otherCorps.length > 0 && <SectionDivider />}
                    </>
                )}
                {otherCorps.length > 0 && (
                    <>
                        {favoriteCorps.length > 0 && (
                            <SectionLabel>All Organizations</SectionLabel>
                        )}
                        <ProgressiveList
                            items={otherCorps.map((corp) => [
                                corp.Id,
                                <CorpTreeItem key={corp.Id} corporation={corp} />,
                            ])}
                        />
                    </>
                )}
            </TreeContainer>
        </PopupContent>
    );
}
